import {
  SNIPPET_HEIGHT_MESSAGE_TYPE,
  SNIPPET_MAX_HEIGHT,
  SNIPPET_MIN_HEIGHT,
  SNIPPET_SANDBOX,
} from './sandbox-constants';
import { buildSnippetSrcdoc } from './build-snippet-srcdoc';

let snippetNonceCounter = 0;

/**
 * Generates a per-iframe nonce used to correlate resize messages.
 *
 * @returns {string}
 */
export function generateSnippetNonce(): string {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  snippetNonceCounter += 1;
  return `omq-snippet-${snippetNonceCounter}`;
}

/**
 * Attaches a window message listener that resizes the iframe to the height
 * reported by its own (and only its own) sandboxed content.
 *
 * @param iframe - the snippet iframe
 * @param nonce - the iframe's nonce; messages must echo it
 *
 * @returns cleanup function removing the listener
 */
export function attachSnippetResize(iframe: HTMLIFrameElement, nonce: string): () => void {
  const onMessage = (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) {
      return;
    }
    const data = event.data;
    if (data == null || data.type !== SNIPPET_HEIGHT_MESSAGE_TYPE || data.id !== nonce) {
      return;
    }
    const height = Number(data.height);
    if (!Number.isFinite(height) || height <= 0) {
      return;
    }
    iframe.style.height = `${Math.min(Math.max(height, SNIPPET_MIN_HEIGHT), SNIPPET_MAX_HEIGHT)}px`;
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/**
 * Creates a sandboxed iframe rendering the given snippet html.
 *
 * @param html - raw snippet html
 * @param opts - optional theme vars injected into the iframe :root, accessible
 *   name for the iframe (title), and BCP47 lang tag for the iframe document
 *
 * @returns the iframe element and its nonce
 */
export function createSnippetIframe(
  html: string,
  opts: { theme?: Record<string, string>; title?: string; lang?: string } = {},
): { iframe: HTMLIFrameElement; nonce: string } {
  const nonce = generateSnippetNonce();
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', SNIPPET_SANDBOX);
  if (opts.title) {
    iframe.title = opts.title;
  }
  iframe.style.width = '100%';
  iframe.style.border = '0';
  iframe.style.display = 'block';
  iframe.style.height = `${SNIPPET_MIN_HEIGHT}px`;
  iframe.srcdoc = buildSnippetSrcdoc(html, { nonce, theme: opts.theme, lang: opts.lang });
  return { iframe, nonce };
}
